import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BookingCalendar from './components/BookingCalendar';
import TimeslotPopup from './components/TimeslotPopup';
import { services } from './components/Services';
import { apiUrl } from './config';
import './components/BookingCalendar.css';
import './BookingPage.css';

const STATE_OPTIONS = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const LOCKABLE_SERVICES = new Set([
  'House Sitting',
  'Dog Walking',
  'Drop-In Visit',
  'Pet Day Care'
]);

const BookingPage = () => {
  const navigate = useNavigate();
  const bookingFormRef = useRef(null);
  const submitModeRef = useRef('pay');
  const [searchParams] = useSearchParams();
  const preselectedServiceTitle = React.useMemo(() => {
    const requestedService = searchParams.get('service');
    if (!requestedService) return '';
    const matchedService = services.find(s => s.title === requestedService);
    return matchedService?.title || '';
  }, [searchParams]);

  const lockedServiceTitle = React.useMemo(() => {
    const shouldLock = searchParams.get('locked') === '1';
    if (!shouldLock || !LOCKABLE_SERVICES.has(preselectedServiceTitle)) return '';
    return preselectedServiceTitle;
  }, [preselectedServiceTitle, searchParams]);

  const shouldHideHouseSittingOption =
    Boolean(preselectedServiceTitle) && preselectedServiceTitle !== 'House Sitting';
  const isHouseSittingOnly = lockedServiceTitle === 'House Sitting';
  const headingServiceTitle = lockedServiceTitle || preselectedServiceTitle;
  const bookingHeading = headingServiceTitle ? `Book a ${headingServiceTitle}` : 'Book a Service';

  const [selectedDates, setSelectedDates] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [dogImages, setDogImages] = useState([]);
  const [phone, setPhone] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [houseSittingStartTime, setHouseSittingStartTime] = useState('');
  const [houseSittingEndTime, setHouseSittingEndTime] = useState('');
  const [houseSittingConsultationConfirmed, setHouseSittingConsultationConfirmed] = useState(false);
  const sortedSelectedDates = React.useMemo(() => [...selectedDates].sort(), [selectedDates]);
  const houseSittingStartDate = sortedSelectedDates[0] || '';
  const houseSittingEndDate = sortedSelectedDates[sortedSelectedDates.length - 1] || '';
  const houseSittingNightCount = Math.max(selectedDates.length - 1, 0);
  // For each selected date, store { frequency, times: [time1, time2, ...], busyTimes: ['09:00', ...] }
    const [dateDetails, setDateDetails] = useState({});
    // Fetch and store busyTimes (flat array of 'HH:MM' strings) for each selected date
    React.useEffect(() => {
      selectedDates.forEach(date => {
        if (!dateDetails[date] || !dateDetails[date].busyTimes) {
          import('./fetchBusyRanges').then(mod => mod.fetchBusyRanges(date)).then(busyRanges => {
            // Convert busyRanges [{start, end}] to all 30-min slots covered
            const slots = [];
            busyRanges.forEach(({ start, end }) => {
              let [h, m] = start.split(':').map(Number);
              const [eh, em] = end.split(':').map(Number);
              while (h < eh || (h === eh && m < em)) {
                slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                m += 30;
                if (m >= 60) { m = 0; h += 1; }
              }
            });
            setDateDetails(prev => ({
              ...prev,
              [date]: {
                ...prev[date],
                busyTimes: slots
              }
            }));
          }).catch(() => {
            setDateDetails(prev => ({
              ...prev,
              [date]: {
                ...prev[date],
                busyTimes: []
              }
            }));
          });
        }
      });
      // eslint-disable-next-line
    }, [selectedDates]);

  React.useEffect(() => {
    if (!lockedServiceTitle || selectedDates.length === 0) return;

    setDateDetails(prev => {
      let changed = false;
      const next = { ...prev };

      selectedDates.forEach(date => {
        const current = next[date] || {};
        if (current.service !== lockedServiceTitle) {
          next[date] = {
            ...current,
            service: lockedServiceTitle,
            times: current.times || Array(Number(current.frequency) || 0).fill({ start: '', end: '' })
          };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [lockedServiceTitle, selectedDates]);

  React.useEffect(() => {
    if (!preselectedServiceTitle || lockedServiceTitle || selectedDates.length === 0) return;

    setDateDetails(prev => {
      let changed = false;
      const next = { ...prev };

      selectedDates.forEach(date => {
        const current = next[date] || {};
        if (!current.service) {
          next[date] = {
            ...current,
            service: preselectedServiceTitle,
            times: current.times || Array(Number(current.frequency) || 0).fill({ start: '', end: '' })
          };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [lockedServiceTitle, preselectedServiceTitle, selectedDates]);

  const [popupInfo, setPopupInfo] = useState({ date: null, idx: null });

  const handleImageChange = e => {
    setDogImages(Array.from(e.target.files));
  };

  const handleFrequencyChange = (date, value) => {
    setDateDetails(prev => {
      const prevEntry = prev[date] || {};
      const oldTimes = Array.isArray(prevEntry.times) ? prevEntry.times : [];
      const newFreq = Number(value) || 0;
      // each time entry will be an object { start: '', end: '' }
      const newTimes = Array.from({ length: newFreq }, (_, i) => oldTimes[i] || { start: '', end: '' });
      return {
        ...prev,
        [date]: {
          ...prevEntry,
          frequency: value,
          times: newTimes
        }
      };
    });
  };

  const handleTimeChange = (date, idx, field, value) => {
    setDateDetails(prev => {
      const entry = prev[date] || { times: [] };
      const times = Array.isArray(entry.times) ? entry.times.slice() : [];
      const current = times[idx] || { start: '', end: '' };
      times[idx] = { ...current, [field]: value };
      return { ...prev, [date]: { ...entry, times } };
    });
  };

  const handleServiceChange = (date, value) => {
    setDateDetails(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        service: value,
        times: prev[date]?.times || Array(Number(prev[date]?.frequency) || 0).fill({ start: '', end: '' })
      }
    }));
  };

  // Time select bounds and generator (30-minute increments)
  const MIN_TIME = '00:00';
  const MAX_TIME = '23:30';
  const STEP_MINUTES = 30;
  const generateTimeOptions = (min, max, step) => {
    const pad = (n) => n.toString().padStart(2, '0');
    const [minH, minM] = min.split(':').map(Number);
    const [maxH, maxM] = max.split(':').map(Number);
    const start = new Date(); start.setHours(minH, minM, 0, 0);
    const end = new Date(); end.setHours(maxH, maxM, 0, 0);
    const opts = [];
    for (let t = start.getTime(); t <= end.getTime(); t += step * 60000) {
      const d = new Date(t);
      const hh = pad(d.getHours());
      const mm = pad(d.getMinutes());
      opts.push(`${hh}:${mm}`);
    }
    return opts;
  };
  const timeOptions = generateTimeOptions(MIN_TIME, MAX_TIME, STEP_MINUTES);
  const format12 = (hhmm) => {
    const [hh, mm] = hhmm.split(':').map(Number);
    const ampm = hh >= 12 ? 'PM' : 'AM';
    let hour12 = hh % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:${mm.toString().padStart(2, '0')} ${ampm}`;
  };
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(`${dateStr}T00:00:00`);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPriceForDate = date => {
    const svcTitle = dateDetails[date]?.service;
    if (!svcTitle) return '';
    const svc = services.find(s => s.title === svcTitle);
    if (!svc || !svc.price) return '';
    // parse price and unit
    const priceMatch = String(svc.price).match(/([0-9]+(?:\.[0-9]+)?)/);
    const base = priceMatch ? parseFloat(priceMatch[1]) : 0;

    const times = Array.isArray(dateDetails[date]?.times) ? dateDetails[date].times : [];
    let total = 0;
    times.forEach(t => {
      // t can be string (legacy) or {start,end}
      if (typeof t === 'string') {
        // legacy: count as one occurrence
        total += base;
      } else if (t && (t.start || t.end)) {
        const start = t.start || '';
        const end = t.end || '';
        if (start && end) {
          const toM = s => {
            const [h, m] = s.split(':').map(Number);
            return h * 60 + m;
          };
          let diff = toM(end) - toM(start);
          if (diff <= 0) diff += 24 * 60;
          total += base * (diff / 60);
        } else {
          total += base;
        }
      }
    });

    const formatted = Number.isInteger(total) ? String(total) : total.toFixed(2);
    return `$${formatted}`;
  };

  const computeGrandTotal = () => {
    if (isHouseSittingOnly) {
      const svc = services.find(s => s.title === 'House Sitting');
      const base = svc ? (parseFloat(String(svc.price).replace(/[^0-9.]/g, '')) || 0) : 0;
      const total = base * houseSittingNightCount;
      return Number.isInteger(total) ? `$${total}` : `$${total.toFixed(2)}`;
    }

    const getNumericTotalForDate = date => {
      const svcTitle = dateDetails[date]?.service;
      if (!svcTitle) return 0;
      const svc = services.find(s => s.title === svcTitle);
      if (!svc || !svc.price) return 0;
      const priceMatch = String(svc.price).match(/([0-9]+(?:\.[0-9]+)?)/);
      const base = priceMatch ? parseFloat(priceMatch[1]) : 0;

      const times = Array.isArray(dateDetails[date]?.times) ? dateDetails[date].times : [];
      let total = 0;
      times.forEach(t => {
        if (typeof t === 'string') {
          total += base;
        } else if (t && (t.start || t.end)) {
          const start = t.start || '';
          const end = t.end || '';
          if (start && end) {
            const toM = s => {
              const [h, m] = s.split(':').map(Number);
              return h * 60 + m;
            };
            let diff = toM(end) - toM(start);
            if (diff <= 0) diff += 24 * 60;
            total += base * (diff / 60);
          } else {
            total += base;
          }
        }
      });
      return total;
    };

    const total = selectedDates.reduce((sum, d) => sum + getNumericTotalForDate(d), 0);
    return Number.isInteger(total) ? `$${total}` : `$${total.toFixed(2)}`;
  };

  const resetFormState = () => {
    setSelectedDates([]);
    setFirstName('');
    setLastName('');
    setEmail('');
    setAddressLine1('');
    setAddressLine2('');
    setStateCode('');
    setZipCode('');
    setPhone('');
    setDogImages([]);
    setDateDetails({});
    setAdditionalNotes('');
    setHouseSittingStartTime('');
    setHouseSittingEndTime('');
    setHouseSittingConsultationConfirmed(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const requestConsultation = submitModeRef.current === 'consultation';

    try {
      const formData = new FormData();
      const payloadDateDetails = { ...dateDetails };

      if (isHouseSittingOnly && houseSittingStartDate && houseSittingEndDate) {
        payloadDateDetails.houseSittingRange = {
          service: 'House Sitting',
          startDate: houseSittingStartDate,
          endDate: houseSittingEndDate,
          startTime: houseSittingStartTime,
          endTime: houseSittingEndTime
        };
      }

      formData.append('firstName', firstName);
      formData.append('lastName', lastName);
      formData.append('email', email);
      formData.append('addressLine1', addressLine1);
      formData.append('addressLine2', addressLine2);
      formData.append('stateCode', stateCode);
      formData.append('zipCode', zipCode);
      formData.append('phone', phone);
      formData.append('selectedDates', JSON.stringify(selectedDates));
      formData.append('dateDetails', JSON.stringify(payloadDateDetails));
      formData.append('additionalNotes', additionalNotes);
      formData.append('consultationRequested', String(requestConsultation));
      dogImages.forEach(file => formData.append('dogImages', file));

      const res = await fetch(apiUrl('/api/bookings'), {
        method: 'POST',
        body: formData
      });

      if (!res.ok) throw new Error('Failed to submit booking');

      const data = await res.json();
      if (data.success) {
        if (requestConsultation) {
          resetFormState();
          navigate('/consultation-success');
          return;
        }

        // Booking created on the server. Now create a Stripe Checkout session and redirect.
        // Compute numeric total in dollars
        const getNumericTotalForDate = date => {
          const svcTitle = dateDetails[date]?.service;
          if (!svcTitle) return 0;
          const svc = services.find(s => s.title === svcTitle);
          if (!svc || !svc.price) return 0;
          const base = parseFloat(String(svc.price).replace(/[^0-9.]/g, '')) || 0;
          const freq = Number(dateDetails[date]?.frequency) || 1;
          return base * freq;
        };

        const total = isHouseSittingOnly
          ? (() => {
            const svc = services.find(s => s.title === 'House Sitting');
            const base = svc ? (parseFloat(String(svc.price).replace(/[^0-9.]/g, '')) || 0) : 0;
            return base * houseSittingNightCount;
          })()
          : selectedDates.reduce((sum, d) => sum + getNumericTotalForDate(d), 0);
        const amountCents = Math.round(total * 100);

        // Call server to create a Checkout session
        const sessRes = await fetch(apiUrl('/create-checkout-session'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookingId: data.booking._id, amount: amountCents, currency: 'usd' })
        });

        if (!sessRes.ok) {
          let checkoutError = 'Failed to create checkout session';
          try {
            const errJson = await sessRes.json();
            checkoutError = errJson?.error || checkoutError;
          } catch (_) {
            // Keep fallback message when error payload is not JSON.
          }
          throw new Error(checkoutError);
        }
        const sessJson = await sessRes.json();
        if (!sessJson.url) throw new Error('No checkout url returned');

        // Redirect user to Stripe Checkout
        window.location.href = sessJson.url;
        resetFormState();
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err) {
      console.error(err);
      alert('Error submitting booking: ' + err.message);
    }
  };

  return (
    <div className="App">
      <div className="banner">
        <h1>{bookingHeading}</h1>
        <button
          style={{
            marginTop: '1rem',
            background: '#fff0f6',
            color: '#d6336c',
            border: '1.5px solid #ffb3c6',
            borderRadius: '0.7rem',
            padding: '0.5rem 1.2rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
          onClick={() => navigate('/')}
        >
          ← Back to Main Page
        </button>
      </div>
      <div className="booking-layout">
        <div className="booking-calendar-side">
          <BookingCalendar
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            enableRangeSelection={isHouseSittingOnly}
          />
          {selectedDates.length > 0 && (
            <div style={{marginTop: '2rem'}}>
              {!isHouseSittingOnly && (
                <label style={{fontWeight: 'bold', color: '#d6336c', display: 'block', marginBottom: '1.5rem'}}>For each selected day, specify the number of times you would like me to come:</label>
              )}
              {isHouseSittingOnly ? (
                <div style={{marginBottom: '1.5rem', background: '#ffe0ef', borderRadius: '0.7rem', padding: '1rem', border: '1.5px solid #ffb3c6'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem'}}>
                    <strong>House Sitting</strong>
                  </div>
                  <p style={{margin: '0 0 0.9rem', color: '#8a1847', lineHeight: 1.45}}>
                    Since this will be a house sitting, I would love to get to know you and your pet(s) first! Please click the "Request a Consultation" button at the end and I will get in touch with you shortly. After the consultation, you can finalize the booking details and make your payment.
                  </p>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap'}}>
                    <div style={{display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', gap: '0.2rem', flexWrap: 'wrap'}}>
                      <div style={{flex: '0 0 auto'}}>
                        <label style={{display: 'block', color: '#d6336c', fontWeight: 'bold', marginBottom: '0.35rem'}}>
                          {formatDisplayDate(houseSittingStartDate)}
                        </label>
                        <select
                          className="booking-input"
                          value={houseSittingStartTime}
                          onChange={e => setHouseSittingStartTime(e.target.value)}
                          required
                        >
                          <option value="" disabled>Select start time</option>
                          {timeOptions.map(t => (
                            <option key={t} value={t}>{format12(t)}</option>
                          ))}
                        </select>
                      </div>
                      <span style={{fontSize: '0.85rem', color: '#666', marginBottom: '0.7rem'}}>to</span>
                      <div style={{flex: '0 0 auto'}}>
                        <label style={{display: 'block', color: '#d6336c', fontWeight: 'bold', marginBottom: '0.35rem'}}>
                          {formatDisplayDate(houseSittingEndDate || houseSittingStartDate)}
                        </label>
                        <select
                          className="booking-input"
                          value={houseSittingEndTime}
                          onChange={e => setHouseSittingEndTime(e.target.value)}
                          required
                        >
                          <option value="" disabled>Select end time</option>
                          {timeOptions.map(t => (
                            <option key={t} value={t}>{format12(t)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{marginLeft: 'auto', color: '#d6336c', textAlign: 'right'}}>
                      <div style={{fontSize: '0.95rem'}}>Nights: {houseSittingNightCount}</div>
                      <div style={{fontSize: '0.95rem'}}>
                        Rate: {(services.find(s => s.title === 'House Sitting')?.price) || '$0/night'}
                      </div>
                      <div style={{fontWeight: 'bold', marginTop: '0.3rem'}}>Subtotal: {computeGrandTotal()}</div>
                    </div>
                  </div>
                </div>
              ) : selectedDates.map(date => (
                <div key={date} style={{marginBottom: '1.5rem', background: '#ffe0ef', borderRadius: '0.7rem', padding: '1rem', border: '1.5px solid #ffb3c6'}}>
                  <div style={{marginBottom: '0.5rem'}}>
                    <strong>{formatDisplayDate(date)}</strong>
                  </div>
                  <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center'}}>
                    <select
                      value={dateDetails[date]?.service || lockedServiceTitle || preselectedServiceTitle || ''}
                      onChange={e => handleServiceChange(date, e.target.value)}
                      className="booking-input"
                      style={{minWidth: 160}}
                      disabled={Boolean(lockedServiceTitle)}
                    >
                      {!lockedServiceTitle && <option value="">Select service</option>}
                      {(lockedServiceTitle
                        ? services.filter(s => s.title === lockedServiceTitle)
                        : shouldHideHouseSittingOption
                          ? services.filter(s => s.title !== 'House Sitting')
                          : services
                      ).map((s, i) => (
                        <option key={i} value={s.title}>{s.title}</option>
                      ))}
                    </select>
                    {[1,2,3].map(n => (
                      <button
                        key={n}
                        type="button"
                        className={dateDetails[date]?.frequency === String(n) ? 'booking-input selected' : 'booking-input'}
                        style={{width: 50, textAlign: 'center', background: dateDetails[date]?.frequency === String(n) ? '#d6336c' : '#fff', color: dateDetails[date]?.frequency === String(n) ? '#fff' : '#d6336c', border: '1.5px solid #ffb3c6', borderRadius: '0.7rem', cursor: 'pointer'}}
                        onClick={() => handleFrequencyChange(date, String(n))}
                      >
                        {n}
                      </button>
                    ))}
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="booking-input"
                      style={{width: 60}}
                      value={dateDetails[date]?.frequency && !['1','2','3'].includes(dateDetails[date]?.frequency) ? dateDetails[date]?.frequency : ''}
                      onChange={e => handleFrequencyChange(date, e.target.value.replace(/\D/g, ''))}
                      placeholder="Custom"
                      autoComplete="off"
                    />
                    <div style={{marginLeft: 'auto', color: '#d6336c', fontWeight: 'bold'}}>
                      {formatPriceForDate(date)}
                    </div>
                  </div>
                  {dateDetails[date]?.frequency && Number(dateDetails[date]?.frequency) > 0 && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                      {[...Array(Number(dateDetails[date]?.frequency))].map((_, idx) => (
                        <React.Fragment key={idx}>
                          <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                              <span style={{color: '#d6336c'}}>Sitting {idx + 1}:</span>
                              <select
                                className="booking-input"
                                value={dateDetails[date]?.times[idx]?.start || ''}
                                onChange={e => handleTimeChange(date, idx, 'start', e.target.value)}
                                required
                              >
                                <option value="" disabled>Start time</option>
                                {timeOptions.map(t => {
                                  const busyTimes = (dateDetails[date]?.busyTimes || []); // e.g. ['09:00', '09:30', ...]
                                  const conflicted = busyTimes.includes(t);
                                  return (
                                    <option key={t} value={t} disabled={conflicted}>{format12(t)}{conflicted ? ' (busy)' : ''}</option>
                                  );
                                })}
                              </select>
                            </div>
                            <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                              <label style={{fontSize: '0.85rem', color: '#666'}}>to</label>
                              <select
                                className="booking-input"
                                value={dateDetails[date]?.times[idx]?.end || ''}
                                onChange={e => handleTimeChange(date, idx, 'end', e.target.value)}
                                required
                              >
                                <option value="" disabled>End time</option>
                                {timeOptions.map(t => {
                                  const busyTimes = (dateDetails[date]?.busyTimes || []);
                                  const conflicted = busyTimes.includes(t);
                                  const startVal = dateDetails[date]?.times[idx]?.start;
                                  let afterStart = true;
                                  let beforeFirstBusy = true;
                                  if (startVal) {
                                    const [sh, sm] = startVal.split(':').map(Number);
                                    const [eh, em] = t.split(':').map(Number);
                                    afterStart = (eh > sh) || (eh === sh && em > sm);
                                    // Find the first busy slot after the selected start time
                                    const busyAfterStart = busyTimes.filter(opt => {
                                      const [bh, bm] = opt.split(':').map(Number);
                                      return (bh > sh) || (bh === sh && bm > sm);
                                    });
                                    if (busyAfterStart.length > 0) {
                                      const firstBusy = busyAfterStart.sort()[0];
                                      const [fbh, fbm] = firstBusy.split(':').map(Number);
                                      // Only allow end times before the first busy slot
                                      if ((eh > fbh) || (eh === fbh && em >= fbm)) beforeFirstBusy = false;
                                    }
                                  }
                                  return (
                                    <option key={t} value={t} disabled={conflicted || !afterStart || !beforeFirstBusy}>
                                      {format12(t)}
                                      {conflicted ? ' (busy)' : !afterStart ? ' (before start)' : !beforeFirstBusy ? ' (overlap)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPopupInfo(prev => (prev.date === date && prev.idx === idx) ? { date: null, idx: null } : { date, idx })}
                              title="Open timeslot picker"
                              style={{marginLeft: 6, background: '#fff', border: '1px solid #ffb3c6', borderRadius: 8, padding: '6px 8px', cursor: 'pointer'}}
                            >
                              ⏰ Check Availability
                            </button>
                          </div>
                          {/* Render popup directly under the respective row */}
                          {popupInfo.date === date && popupInfo.idx === idx && (
                            <div style={{marginTop: 8}}>
                              <TimeslotPopup
                                dateStr={date}
                                onSelect={(slot) => {
                                  const pad = (n) => String(n).padStart(2, '0');
                                  const hh = slot.start.getHours();
                                  const mm = slot.start.getMinutes();
                                  const hh2 = slot.end.getHours();
                                  const mm2 = slot.end.getMinutes();
                                  const startStr = `${pad(hh)}:${pad(mm)}`;
                                  const endStr = `${pad(hh2)}:${pad(mm2)}`;
                                  handleTimeChange(date, popupInfo.idx, 'start', startStr);
                                  handleTimeChange(date, popupInfo.idx, 'end', endStr);
                                  setPopupInfo({ date: null, idx: null });
                                }}
                              />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <form className="booking-form" onSubmit={handleSubmit} ref={bookingFormRef}>
          <div className="booking-input-row">
            <input className="booking-input" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First Name" required />
            <input className="booking-input" type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last Name" required />
          </div>
          <input className="booking-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required />
          <input className="booking-input" type="text" value={addressLine1} onChange={e => setAddressLine1(e.target.value)} placeholder="Address Line 1" required />
          <input className="booking-input" type="text" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} placeholder="Address Line 2" />
          <div className="booking-input-row">
            <select className="booking-input" value={stateCode} onChange={e => setStateCode(e.target.value)} required>
              <option value="" disabled>Select State</option>
              {STATE_OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <input className="booking-input" type="text" value={zipCode} onChange={e => setZipCode(e.target.value.replace(/[^0-9-]/g, ''))} placeholder="ZIP Code" required inputMode="numeric" pattern="[0-9]{5}(?:-[0-9]{4})?" />
          </div>
          <input className="booking-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone Number" required pattern="[0-9\-\+\s\(\)]*" />
          <label className="upload-label">
            <span>Upload Dog Pictures</span>
            <input type="file" multiple accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
          </label>
          <textarea
            className="booking-textarea"
            value={additionalNotes}
            onChange={e => setAdditionalNotes(e.target.value)}
            placeholder="Additional notes for me (allergies, meds, quirks, feeding instructions...)"
          />
          <div className="dog-image-grid">
            {dogImages.map((img, idx) => (
              <img key={idx} src={URL.createObjectURL(img)} alt="dog" className="dog-image-thumb" />
            ))}
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {isHouseSittingOnly && (
              <>
                <button
                  className="booking-submit booking-submit-secondary"
                  type="button"
                  onClick={() => {
                    submitModeRef.current = 'consultation';
                    bookingFormRef.current?.requestSubmit();
                  }}
                >
                  Request a Consultation
                </button>
                <label className="booking-confirmation-check">
                  <input
                    type="checkbox"
                    checked={houseSittingConsultationConfirmed}
                    onChange={e => setHouseSittingConsultationConfirmed(e.target.checked)}
                  />
                  <span>
                    I confirm that we have completed our consultation, agree to the booking terms, and I am ready to proceed with payment.
                  </span>
                </label>
              </>
            )}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div style={{color: '#d6336c', fontWeight: 'bold', fontSize: '1.1rem'}}>{computeGrandTotal()}</div>
              <button
                className="booking-submit"
                type="submit"
                disabled={isHouseSittingOnly && !houseSittingConsultationConfirmed}
                onClick={() => {
                  submitModeRef.current = 'pay';
                }}
              >
                Book & Pay Now
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingPage;
